import { Navigate, createBrowserRouter } from 'react-router';
import type { ReactNode } from 'react';
import type { Capability } from '@pos/core';
import { AppLayout } from './AppLayout.tsx';
import { useSession } from './current.tsx';
import { HomePage } from '../features/home/HomePage.tsx';
import { PosPage } from '../features/pos/PosPage.tsx';
import { SettingsLayout } from '../features/settings/SettingsLayout.tsx';
import { BusinessSettingsPage } from '../features/settings/BusinessSettingsPage.tsx';
import { EmployeesPage } from '../features/settings/EmployeesPage.tsx';
import { DevicesPage } from '../features/settings/DevicesPage.tsx';
import { ProductsPage } from '../features/products/ProductsPage.tsx';
import { ProductFormPage } from '../features/products/ProductFormPage.tsx';
import { ImportPage } from '../features/products/ImportPage.tsx';
import { BulkPricePage } from '../features/products/BulkPricePage.tsx';
import { CategoriesPage } from '../features/products/CategoriesPage.tsx';
import { SuppliersPage } from '../features/products/SuppliersPage.tsx';
import { ActivityPage } from '../features/activity/ActivityPage.tsx';
import { StockPage } from '../features/stock/StockPage.tsx';
import { PurchasePage } from '../features/stock/PurchasePage.tsx';
import { PurchasesPage } from '../features/stock/PurchasesPage.tsx';
import { CountPage } from '../features/stock/CountPage.tsx';
import { MovementsPage } from '../features/stock/MovementsPage.tsx';
import { SalesPage } from '../features/sales/SalesPage.tsx';
import { ReportsPage } from '../features/reports/ReportsPage.tsx';
import { CashPage } from '../features/cash/CashPage.tsx';
import { CashHistoryPage } from '../features/cash/CashHistoryPage.tsx';
import { CustomerDetailPage, CustomersPage } from '../features/customers/CustomersPage.tsx';
import { PosSettingsPage } from '../features/settings/PosSettingsPage.tsx';
import { RegistersPage } from '../features/settings/RegistersPage.tsx';
import { SyncPage } from '../features/sync/SyncPage.tsx';

/** Si el usuario no tiene el permiso, lo manda al inicio (el servidor igual lo rechazaría). */
function RequireCapability({ capability, children }: { capability: Capability; children: ReactNode }) {
  const { can } = useSession();
  return can(capability) ? children : <Navigate to="/" replace />;
}

/** El dueño entra al tablero; el empleado directo a vender. */
function Home() {
  const { isOwner } = useSession();
  return isOwner ? <HomePage /> : <Navigate to="/vender" replace />;
}

export const router = createBrowserRouter([
  {
    element: <AppLayout />,
    children: [
      { index: true, element: <Home /> },
      { path: 'vender', element: <PosPage /> },
      { path: 'ventas', element: <SalesPage /> },
      { path: 'caja', element: <CashPage /> },
      { path: 'caja/historial', element: <RequireCapability capability="view_reports"><CashHistoryPage /></RequireCapability> },
      { path: 'clientes', element: <CustomersPage /> },
      { path: 'reportes', element: <RequireCapability capability="view_reports"><ReportsPage /></RequireCapability> },
      { path: 'clientes/:id', element: <CustomerDetailPage /> },
      { path: 'sincronizacion', element: <SyncPage /> },
      { path: 'productos', element: <ProductsPage /> },
      ...(
        [
          ['productos/nuevo', <ProductFormPage />],
          ['productos/importar', <ImportPage />],
          ['productos/aumento', <BulkPricePage />],
          ['productos/categorias', <CategoriesPage />],
          ['productos/proveedores', <SuppliersPage />],
          ['productos/:id', <ProductFormPage />],
        ] as const
      ).map(([path, element]) => ({
        path,
        element: <RequireCapability capability="manage_products">{element}</RequireCapability>,
      })),
      { path: 'stock', element: <StockPage /> },
      { path: 'stock/movimientos/:variantId', element: <MovementsPage /> },
      { path: 'stock/compra', element: <RequireCapability capability="receive_purchases"><PurchasePage /></RequireCapability> },
      { path: 'stock/compras', element: <RequireCapability capability="view_costs"><PurchasesPage /></RequireCapability> },
      { path: 'stock/conteo', element: <RequireCapability capability="adjust_stock"><CountPage /></RequireCapability> },
      {
        path: 'actividad',
        element: (
          <RequireCapability capability="view_reports">
            <ActivityPage />
          </RequireCapability>
        ),
      },
      {
        path: 'configuracion',
        element: (
          <RequireCapability capability="manage_settings">
            <SettingsLayout />
          </RequireCapability>
        ),
        children: [
          { index: true, element: <BusinessSettingsPage /> },
          { path: 'ventas', element: <PosSettingsPage /> },
          { path: 'cajas', element: <RegistersPage /> },
          { path: 'empleados', element: <EmployeesPage /> },
          { path: 'dispositivos', element: <DevicesPage /> },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
